import { Link } from "react-router-dom";
import { ArrowDown, Crown } from "lucide-react";
import { dateTime, shortDate } from "@/lib/pricing";
import { STATUS_META } from "@/lib/team";
import { TeamRowActions, allowedActions } from "@/components/admin/team/TeamRowActions";

export function StatusChip({ status, testid }) {
  const [label, cls] = STATUS_META[status] || [status, "pf-chip-lav"];
  return <span className={`pf-chip ${cls}`} data-testid={testid}>{label}</span>;
}

function SortTh({ id, sort, onSort, children }) {
  return (
    <th className="px-4 py-3 whitespace-nowrap">
      <button type="button" onClick={() => onSort(id)} className={`inline-flex items-center gap-1 uppercase tracking-widest ${sort === id ? "text-slate-900" : ""}`} data-testid={`team-sort-${id}`}>
        {children}{sort === id && <ArrowDown className="w-3 h-3" />}
      </button>
    </th>
  );
}

function Row({ r, actor, actorIsOwner, onAction }) {
  const you = r.kind === "member" && r.id === actor?.id;
  return (
    <tr className="border-t border-white/60 align-top" data-testid={`team-row-${r.id}`}>
      <td className="px-4 py-3">
        {r.kind === "member"
          ? <Link to={`/admin/team/${r.id}`} className="font-medium text-slate-900 hover:underline" data-testid={`team-row-name-${r.id}`}>{r.name || "—"}</Link>
          : <span className="font-medium text-slate-900" data-testid={`team-row-name-${r.id}`}>{r.name}</span>}
        <div className="flex gap-1 mt-1 flex-wrap">
          {r.is_owner && <span className="pf-chip pf-chip-cyan" data-testid={`team-row-owner-${r.id}`}><Crown className="w-3 h-3" /> Main owner</span>}
          {you && <span className="pf-chip" data-testid={`team-row-you-${r.id}`}>You</span>}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`team-row-email-${r.id}`}>{r.email}</td>
      <td className="px-4 py-3 text-slate-800 whitespace-nowrap" data-testid={`team-row-role-${r.id}`}>{r.role_label}</td>
      <td className="px-4 py-3"><StatusChip status={r.status} testid={`team-row-status-${r.id}`} /></td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{r.invited_by === "system@cyannect" ? "Automatic (owner setup)" : r.invited_by || "—"}</td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{r.invited_at ? shortDate(r.invited_at) : "—"}</td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`team-row-last-login-${r.id}`}>{r.last_login_at ? dateTime(r.last_login_at) : "Never"}</td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{r.created_at ? shortDate(r.created_at) : "—"}</td>
      <td className="px-4 py-3 text-right sticky right-0 bg-white/95">
        <TeamRowActions row={r} actions={allowedActions(r, actor, actorIsOwner)} onAction={onAction} />
      </td>
    </tr>
  );
}

export function TeamTable({ rows, sort, onSort, actor, actorIsOwner, onAction }) {
  return (
    <div className="pf-card overflow-x-auto mt-4">
      <table className="w-full text-sm min-w-[1080px]" data-testid="team-table">
        <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
          <tr>
            <SortTh id="name" sort={sort} onSort={onSort}>Full name</SortTh>
            <th className="px-4 py-3">Email</th>
            <SortTh id="role" sort={sort} onSort={onSort}>Cyannect role</SortTh>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 whitespace-nowrap">Invited by</th>
            <th className="px-4 py-3 whitespace-nowrap">Invitation date</th>
            <SortTh id="last_login" sort={sort} onSort={onSort}>Last login</SortTh>
            <SortTh id="created" sort={sort} onSort={onSort}>Created</SortTh>
            <th className="px-4 py-3 sticky right-0 bg-white/95"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <Row key={`${r.kind}-${r.id}`} r={r} actor={actor} actorIsOwner={actorIsOwner} onAction={onAction} />)}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-500" data-testid="team-empty">No team members match these filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
